import React, { createContext, useContext, useState } from 'react';

/**
 * TDD Context for managing TDD workflow state
 */
const TDDContext = createContext();

/**
 * Hook to use TDD context
 */
export const useTDD = () => {
  const context = useContext(TDDContext);
  if (!context) {
    throw new Error('useTDD must be used within a TDDProvider');
  }
  return context;
};

/**
 * TDD Provider component
 */
export const TDDProvider = ({ children }) => {
  const [tddState, setTDDState] = useState({
    // Workspace information
    workspace: {
      path: null,
      status: 'not_initialized', // not_initialized, initializing, ready, error
      files: [],
      lastUpdated: null
    },
    
    // Current task being worked on
    currentTask: {
      id: null,
      title: null,
      description: null,
      status: 'pending', // pending, in_progress, testing, completed, failed
      atomicTasks: [],
      currentAtomicTask: null
    },
    
    // Test results
    testResults: {
      lastRun: null,
      passed: 0,
      failed: 0,
      results: []
    },
    
    // Generated code
    codeGenerated: {
      files: {},
      lastGenerated: null
    },
    
    // Overall progress
    progress: {
      phase: 'initialization', // initialization, analysis, development, testing, completion
      currentStep: 0,
      totalSteps: 0,
      percentage: 0,
      message: 'Starting TDD workflow...'
    },
    
    // User stories and analysis
    userStories: [],
    analysisResults: null,
    
    // Ralphio TDD specific state
    tddCycle: {
      currentCycle: 0,
      totalCycles: 0,
      cyclePhase: 'red', // red, green, refactor
      cycleHistory: []
    }
  });

  /**
   * Update workspace information
   */
  const updateWorkspace = (workspaceData) => {
    setTDDState(prev => ({
      ...prev,
      workspace: {
        ...prev.workspace,
        ...workspaceData
      }
    }));
  };

  /**
   * Update current task
   */
  const updateCurrentTask = (taskData) => {
    setTDDState(prev => ({
      ...prev,
      currentTask: {
        ...prev.currentTask,
        ...taskData
      }
    }));
  };

  /**
   * Update test results
   */
  const updateTestResults = (results) => {
    setTDDState(prev => ({
      ...prev,
      testResults: {
        ...prev.testResults,
        ...results,
        lastRun: new Date().toISOString()
      }
    }));
  };

  /**
   * Update generated code
   */
  const updateGeneratedCode = (codeData) => {
    setTDDState(prev => ({
      ...prev,
      codeGenerated: {
        ...prev.codeGenerated,
        ...codeData,
        lastGenerated: new Date().toISOString()
      }
    }));
  };

  /**
   * Update progress
   */
  const updateProgress = (progressData) => {
    setTDDState(prev => ({
      ...prev,
      progress: {
        ...prev.progress,
        ...progressData
      }
    }));
  };

  /**
   * Update TDD cycle information
   */
  const updateTDDCycle = (cycleData) => {
    setTDDState(prev => ({
      ...prev,
      tddCycle: {
        ...prev.tddCycle,
        ...cycleData
      }
    }));
  };

  /**
   * Reset all state to initial values
   */
  const resetTDDState = () => {
    setTDDState({
      workspace: {
        path: null,
        status: 'not_initialized',
        files: [],
        lastUpdated: null
      },
      currentTask: {
        id: null,
        title: null,
        description: null,
        status: 'pending',
        atomicTasks: [],
        currentAtomicTask: null
      },
      testResults: {
        lastRun: null,
        passed: 0,
        failed: 0,
        results: []
      },
      codeGenerated: {
        files: {},
        lastGenerated: null
      },
      progress: {
        phase: 'initialization',
        currentStep: 0,
        totalSteps: 0,
        percentage: 0,
        message: 'Starting TDD workflow...'
      },
      userStories: [],
      analysisResults: null,
      tddCycle: {
        currentCycle: 0,
        totalCycles: 0,
        cyclePhase: 'red',
        cycleHistory: []
      }
    });
  };

  const contextValue = {
    tddState,
    setTDDState,
    updateWorkspace,
    updateCurrentTask,
    updateTestResults,
    updateGeneratedCode,
    updateProgress,
    updateTDDCycle,
    resetTDDState
  };

  return (
    <TDDContext.Provider value={contextValue}>
      {children}
    </TDDContext.Provider>
  );
};